
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Check, Globe, Wallet as WalletIcon, ArrowLeft, Mail, KeyRound } from 'lucide-react';
import { useWallet } from '../providers/WalletProvider';
import { useNetwork } from '../hooks/use-network';
import { Network, Chain } from '../types/networks';
import { formatCurrency } from '../lib/utils/format';
import { SignIn } from './auth/SignIn';
import { SignUp } from './auth/SignUp';

interface WalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WALLETS = {
  SOL: [
    { name: 'Phantom', icon: 'https://cryptologos.cc/logos/phantom-phantom-logo.png?v=026' },
    { name: 'Solflare', icon: 'https://cryptologos.cc/logos/solana-sol-logo.png?v=026' },
  ],
  SUI: [
    { name: 'Sui Wallet', icon: 'https://cryptologos.cc/logos/sui-sui-logo.png?v=026' },
    { name: 'Suiet', icon: 'https://assets.website-files.com/635678313177695325247963/63567d264a7538d388277579_Suiet_Logo_Blue.svg' },
  ]
};

type ViewState = 'SELECT' | 'SIGNIN' | 'SIGNUP';

export const WalletModal: React.FC<WalletModalProps> = ({ isOpen, onClose }) => {
  const { connect, isConnecting } = useWallet();
  const { nativePrice } = useNetwork();
  
  const [activeTab, setActiveTab] = useState<Chain>('SOL');
  const [selectedNetwork, setSelectedNetwork] = useState<Network>('devnet');
  const [view, setView] = useState<ViewState>('SELECT');

  // Reset view on close
  useEffect(() => {
      if(!isOpen) setTimeout(() => setView('SELECT'), 300);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConnect = async (walletName: string, embeddedProvider?: string, email?: string) => {
    await connect(activeTab, walletName, selectedNetwork, embeddedProvider, email);
    onClose();
  };

  const handleTabChange = (chain: Chain) => {
      setActiveTab(chain);
  }

  const getTitle = () => {
      if (view === 'SIGNIN') return 'Sign In';
      if (view === 'SIGNUP') return 'Create Account';
      return 'Connect Wallet';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="wallet-modal-title">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      
      <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up border border-stone-100 flex flex-col max-h-[90vh]">
        
        <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-white sticky top-0 z-10">
           <div className="flex items-center gap-3">
               {view !== 'SELECT' ? (
                   <button onClick={() => setView('SELECT')} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors" aria-label="Back to wallets" title="Go Back">
                       <ArrowLeft size={18} className="text-stone-600" />
                   </button>
               ) : (
                   <div className="w-8 h-8 bg-stone-900 rounded-full flex items-center justify-center text-white">
                       <WalletIcon size={16} />
                   </div>
               )}
               <h3 id="wallet-modal-title" className="font-serif text-xl font-bold text-stone-900">{getTitle()}</h3>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-900 transition-colors" aria-label="Close" title="Close">
             <X size={20} />
           </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-stone-50/50">
            {view === 'SELECT' && (
                <div className="space-y-6">
                    {/* Embedded Wallets */}
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                        <h4 className="text-sm font-bold text-stone-900 mb-1 flex items-center gap-2"><KeyRound size={16} className="text-nobel-gold"/> Email & Social Login</h4>
                        <p className="text-xs text-stone-500 mb-4">A non-custodial wallet is created for you instantly. No extension needed.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => setView('SIGNIN')} className="w-full py-3 bg-stone-900 text-white rounded-md font-bold text-xs hover:bg-stone-800 transition-colors shadow-sm">Sign In</button>
                            <button onClick={() => setView('SIGNUP')} className="w-full py-3 bg-white border border-stone-200 text-stone-600 rounded-md font-bold text-xs hover:bg-stone-50 transition-colors">Create Account</button>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <div className="h-px bg-stone-200 flex-1"></div>
                        <span className="text-[10px] text-stone-400 uppercase font-bold tracking-wider">Or</span>
                        <div className="h-px bg-stone-200 flex-1"></div>
                    </div>

                    {/* Extension Wallets */}
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                         <h4 className="text-sm font-bold text-stone-900 mb-4 flex items-center gap-2"><Globe size={16} className="text-stone-500"/> Browser Extension</h4>
                         <div className="flex border-b border-stone-200 mb-4" role="tablist">
                            <button onClick={() => handleTabChange('SOL')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors relative ${activeTab === 'SOL' ? 'text-stone-900' : 'text-stone-400'}`}>
                                <img src="https://cryptologos.cc/logos/solana-sol-logo.png" className="w-4 h-4" alt="" /> SOLANA
                                {activeTab === 'SOL' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-stone-900"></div>}
                            </button>
                             <button onClick={() => handleTabChange('SUI')} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors relative ${activeTab === 'SUI' ? 'text-stone-900' : 'text-stone-400'}`}>
                                <img src="https://cryptologos.cc/logos/sui-sui-logo.png" className="w-4 h-4" alt="" /> SUI
                                {activeTab === 'SUI' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-stone-900"></div>}
                            </button>
                         </div>

                        {isConnecting ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <Loader2 size={24} className="animate-spin text-nobel-gold mb-3" />
                                <p className="text-sm text-stone-900 font-bold">Connecting...</p>
                                <p className="text-xs text-stone-400 mt-1">Check your extension window</p>
                            </div>
                        ) : (
                             <div className="space-y-3">
                                {WALLETS[activeTab].map((wallet) => (
                                    <button
                                        key={wallet.name}
                                        onClick={() => handleConnect(wallet.name)}
                                        className="w-full flex items-center gap-3 p-3 rounded-md border border-stone-200 hover:border-stone-400 transition-all group"
                                    >
                                        <div className="w-8 h-8 rounded-md p-1 flex items-center justify-center shrink-0">
                                            <img src={wallet.icon} alt="" className="w-full h-full object-contain" />
                                        </div>
                                        <span className="font-bold text-sm text-stone-800">{wallet.name}</span>
                                    </button>
                                ))}
                             </div>
                        )}
                    </div>

                    {/* Network Selector */}
                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-stone-200">
                        <button onClick={() => setSelectedNetwork('mainnet')} className={`p-3 rounded-md border text-left transition-all ${selectedNetwork === 'mainnet' ? 'border-green-500 bg-green-50' : 'bg-white border-stone-200'}`}>
                            <span className={`font-bold text-xs ${selectedNetwork === 'mainnet' ? 'text-green-700' : 'text-stone-700'}`}>Mainnet Beta</span>
                        </button>
                        <button onClick={() => setSelectedNetwork('devnet')} className={`p-3 rounded-md border text-left transition-all ${selectedNetwork === 'devnet' ? 'border-amber-400 bg-amber-50' : 'bg-white border-stone-200'}`}>
                            <span className={`font-bold text-xs ${selectedNetwork === 'devnet' ? 'text-amber-700' : 'text-stone-700'}`}>Devnet</span>
                        </button>
                    </div>
                </div>
            )}
            
            {view === 'SIGNIN' && <SignIn onLogin={async (provider, email) => handleConnect('Embedded', provider, email)} />}
            {view === 'SIGNUP' && <SignUp onSignup={async (provider, email) => handleConnect('Embedded', provider, email)} />}
        </div>
      </div>
    </div>
  );
};